import React, { useEffect, useState, useCallback } from 'react';
import { communityApi, getAuthSession } from '../services/api';

const DEFAULT_POSTS = [
  {
    _id: 'default_1',
    authorName: 'Dr. Sarah Johnson',
    isVerifiedTherapist: true,
    therapistSpecialization: 'Clinical Psychologist',
    createdAt: new Date(Date.now() - 2 * 3600000).toISOString(),
    category: 'Mental Health',
    content: 'Just completed a mindfulness session with my patients! Remember that pausing for 3 conscious breaths during a stressful workday activates your parasympathetic nervous system.',
    likesCount: 24,
    commentsCount: 2,
    likes: [],
    comments: [
      {
        _id: 'c_def_1',
        authorName: 'Michael Chen',
        authorRole: 'client',
        content: 'Thank you Dr. Johnson! Taking 3 deep breaths really made a difference in my workday.',
        createdAt: new Date(Date.now() - 1 * 3600000).toISOString(),
      },
      {
        _id: 'c_def_2',
        authorName: 'Anita Roy',
        authorRole: 'client',
        content: 'Practicing this right now. Truly calming!',
        createdAt: new Date(Date.now() - 30 * 60000).toISOString(),
      },
    ],
  },
  {
    _id: 'default_2',
    authorName: 'Michael Chen',
    isVerifiedTherapist: false,
    createdAt: new Date(Date.now() - 5 * 3600000).toISOString(),
    category: 'Success Story',
    content: 'Wanted to share my journey overcoming panic attacks through consistent cognitive behavioral therapy. The progress has been life-changing! Never give up on yourself.',
    likesCount: 156,
    commentsCount: 1,
    likes: [],
    comments: [
      {
        _id: 'c_def_3',
        authorName: 'Dr. Rajesh Sharma',
        authorRole: 'physiotherapist',
        content: 'So proud of your progress Michael! Consistency is key in CBT.',
        createdAt: new Date(Date.now() - 3 * 3600000).toISOString(),
      },
    ],
  },
];

function CommunityPage() {
  const authSession = getAuthSession();
  const currentUserId = authSession?.user?._id || authSession?.user?.id;
  const currentUserName = authSession?.user?.name || 'Member';
  const currentUserRole = authSession?.user?.role || 'client';

  const [posts, setPosts] = useState(DEFAULT_POSTS);
  const [loading, setLoading] = useState(true);
  const [postTitle, setPostTitle] = useState('');
  const [newPost, setNewPost] = useState('');
  const [category, setCategory] = useState('Mental Health');
  const [showPostForm, setShowPostForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Prohibited words detection state for main post
  const [detectedProhibited, setDetectedProhibited] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // Comment section state
  const [expandedComments, setExpandedComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [commentErrors, setCommentErrors] = useState({});
  const [submittingCommentId, setSubmittingCommentId] = useState(null);

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await communityApi.getPosts();
      if (res?.posts && res.posts.length > 0) {
        setPosts(res.posts);
      } else if (res?.posts && Array.isArray(res.posts)) {
        setPosts(res.posts);
      } else {
        setPosts(DEFAULT_POSTS);
      }
    } catch (err) {
      console.warn('Could not load live community posts, using default fallback:', err);
      setPosts(DEFAULT_POSTS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Live real-time scan as user types new post
  useEffect(() => {
    const fullText = `${postTitle} ${newPost}`.trim();
    if (!fullText) {
      setDetectedProhibited([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsScanning(true);
        const res = await communityApi.verifyText(fullText);
        if (res?.hasProhibitedWords && res.foundWords) {
          setDetectedProhibited(res.foundWords);
        } else {
          setDetectedProhibited([]);
        }
      } catch (err) {
        // Silently catch
      } finally {
        setIsScanning(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [postTitle, newPost]);

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;

    if (!authSession?.token) {
      alert('Please log in or register to publish a post on the Community Wall.');
      window.location.href = '/login';
      return;
    }

    if (detectedProhibited.length > 0) {
      setErrorMessage(`Your post contains prohibited words: ${detectedProhibited.map(w => `"${w.word}"`).join(', ')}. Please remove them.`);
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage('');

      const res = await communityApi.createPost({
        title: postTitle.trim(),
        content: newPost.trim(),
        category,
      });

      setStatusMessage('✓ Post published to the community wall!');
      setTimeout(() => setStatusMessage(''), 4000);

      setPostTitle('');
      setNewPost('');
      setShowPostForm(false);
      setDetectedProhibited([]);
      if (res?.post) {
        setPosts(prev => [res.post, ...prev.filter(p => p._id !== res.post._id && !String(p._id).startsWith('default_'))]);
      }
      fetchPosts();
    } catch (err) {
      console.error('Failed to post:', err);
      if (err?.isProhibited && err?.foundWords) {
        setDetectedProhibited(err.foundWords);
      }
      setErrorMessage(err.message || 'Failed to submit post.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLike = async (post) => {
    if (!authSession?.token) {
      alert('Please login to like community posts.');
      return;
    }

    try {
      const res = await communityApi.likePost(post._id);
      setPosts(prev => prev.map(p => {
        if (p._id === post._id) {
          return {
            ...p,
            likesCount: res.likesCount,
            likes: res.isLiked
              ? [...(p.likes || []), currentUserId]
              : (p.likes || []).filter(id => String(id) !== String(currentUserId)),
          };
        }
        return p;
      }));
    } catch (err) {
      // Optimistic toggle fallback
      setPosts(prev => prev.map(p => {
        if (p._id === post._id) {
          return { ...p, likesCount: (p.likesCount || 0) + 1 };
        }
        return p;
      }));
    }
  };

  // Toggle comments expand/collapse
  const handleToggleComments = (postId) => {
    setExpandedComments(prev => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  };

  // Add comment handler (client, therapist, admin)
  const handleAddComment = async (postId) => {
    if (!authSession?.token) {
      if (window.confirm('Please log in or register to join the discussion and post comments. Go to login?')) {
        window.location.href = '/login';
      }
      return;
    }

    const text = (commentInputs[postId] || '').trim();
    if (!text) return;

    try {
      setSubmittingCommentId(postId);
      setCommentErrors(prev => ({ ...prev, [postId]: '' }));

      // Fallback demo posts handle client-side
      if (String(postId).startsWith('default_')) {
        const clientComment = {
          _id: `c_${Date.now()}`,
          author: currentUserId,
          authorName: currentUserName,
          authorRole: currentUserRole,
          authorAvatar: authSession?.user?.avatar || '',
          content: text,
          createdAt: new Date().toISOString(),
        };
        setPosts(prev => prev.map(p => {
          if (p._id === postId) {
            const updated = [...(p.comments || []), clientComment];
            return {
              ...p,
              comments: updated,
              commentsCount: updated.length,
            };
          }
          return p;
        }));
        setCommentInputs(prev => ({ ...prev, [postId]: '' }));
        return;
      }

      const res = await communityApi.addComment(postId, { content: text });
      setPosts(prev => prev.map(p => {
        if (p._id === postId) {
          return {
            ...p,
            comments: res.comments,
            commentsCount: res.commentsCount,
          };
        }
        return p;
      }));
      setCommentInputs(prev => ({ ...prev, [postId]: '' }));
    } catch (err) {
      console.error('Add comment error:', err);
      setCommentErrors(prev => ({
        ...prev,
        [postId]: err.message || 'Failed to submit comment. Please check for prohibited words or try again.'
      }));
    } finally {
      setSubmittingCommentId(null);
    }
  };

  // Delete comment handler
  const handleDeleteComment = async (postId, commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      if (String(postId).startsWith('default_')) {
        setPosts(prev => prev.map(p => {
          if (p._id === postId) {
            const updated = (p.comments || []).filter(c => c._id !== commentId);
            return {
              ...p,
              comments: updated,
              commentsCount: updated.length,
            };
          }
          return p;
        }));
        return;
      }

      const res = await communityApi.deleteComment(postId, commentId);
      setPosts(prev => prev.map(p => {
        if (p._id === postId) {
          return {
            ...p,
            comments: res.comments,
            commentsCount: res.commentsCount,
          };
        }
        return p;
      }));
    } catch (err) {
      alert(err.message || 'Failed to delete comment');
    }
  };

  const formatCommentDate = (dateVal) => {
    if (!dateVal) return 'Just now';
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return 'Recently';
      const now = new Date();
      const diffMs = now - d;
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'Just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHours = Math.floor(diffMin / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays}d ago`;
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch {
      return 'Recently';
    }
  };

  const canDeleteComment = (comment, post) => {
    if (!currentUserId) return false;
    const commentAuthorId = comment?.author?._id || comment?.author?.id || comment?.author;
    const postAuthorId = post?.author?._id || post?.author?.id || post?.author;
    const isCommentAuthor = String(commentAuthorId).toLowerCase() === String(currentUserId).toLowerCase();
    const isPostAuthor = String(postAuthorId).toLowerCase() === String(currentUserId).toLowerCase();
    const isAdmin = authSession?.user?.role === 'admin';
    return isCommentAuthor || isPostAuthor || isAdmin;
  };

  return (
    <>
      {/* Page Header Start */}
      <section className="page-header">
        <div className="page-header__bg" style={{ backgroundImage: 'url(/assets/images/backgrounds/slider-1-1.jpg)' }}></div>
        <div className="container">
          <div className="page-header__inner">
            <h2 className="page-header__title">Community Wall</h2>
            <ul className="thm-breadcrumb">
              <li><a href="/">Home</a></li>
              <li><span>/</span></li>
              <li><span>Community</span></li>
            </ul>
          </div>
        </div>
      </section>
      {/* Page Header End */}

      {/* Community Section Start */}
      <section className="community-section" style={{
        padding: '80px 0',
        background: 'linear-gradient(120deg, #f8fafc 0%, #e0e7ff 100%)',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="section-title text-center wow fadeInUp" style={{ marginBottom: '50px' }}>
                <span className="section-title__tagline" style={{ color: '#7c3aed', fontWeight: 600, fontSize: '1.1rem', letterSpacing: '0.04em' }}>Share & Connect</span>
                <h2 className="section-title__title" style={{ fontSize: '2.5rem', fontWeight: 800, color: '#2d2d4d', marginBottom: '18px' }}>Community Wall</h2>
                <p style={{ color: '#555', fontSize: '1.13rem', maxWidth: '520px', margin: '0 auto' }}>
                  A safe, moderated space for mental wellness insights, therapist clinical advice, and peer support.
                </p>
              </div>
            </div>
          </div>

          {statusMessage && (
            <div style={{ maxWidth: '800px', margin: '0 auto 20px', padding: '12px 18px', background: '#ecfdf5', color: '#065f46', borderRadius: '10px', fontWeight: 600, border: '1px solid #a7f3d0' }}>
              {statusMessage}
            </div>
          )}

          {errorMessage && (
            <div style={{ maxWidth: '800px', margin: '0 auto 20px', padding: '12px 18px', background: '#fef2f2', color: '#991b1b', borderRadius: '10px', fontWeight: 600, border: '1px solid #fecaca' }}>
              {errorMessage}
            </div>
          )}

          <div className="row">
            <div className="col-lg-8">
              {/* Create Post Form */}
              <div className="create-post-box wow fadeInUp" style={{
                background: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(6px)',
                padding: '32px',
                borderRadius: '18px',
                boxShadow: '0 6px 32px rgba(80,80,120,0.13)',
                marginBottom: '44px',
                border: '1.5px solid #f3e9ff',
                transition: 'box-shadow 0.2s, transform 0.2s',
              }}>
                {!showPostForm ? (
                  <button
                    onClick={() => {
                      if (!authSession?.token) {
                        alert('Please login or register to share your thoughts on the Community Wall.');
                        window.location.href = '/login';
                        return;
                      }
                      setShowPostForm(true);
                    }}
                    className="thm-btn"
                    style={{ width: '100%', padding: '18px', fontWeight: 700, fontSize: '1.13rem', borderRadius: '10px', boxShadow: '0 2px 8px rgba(124,58,237,0.08)' }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '1.5rem', color: '#7c3aed', fontWeight: 700 }}>+</span> Share Your Thoughts
                    </span>
                  </button>
                ) : (
                  <form onSubmit={handlePostSubmit}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '14px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>Category:</label>
                        <select
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #d8b4fe', fontSize: '13px', fontWeight: 700, background: '#fff', color: '#7c3aed' }}
                        >
                          <option value="Mental Health">Mental Health</option>
                          <option value="Clinical Insights">Clinical Insights</option>
                          <option value="Therapy & Coping">Therapy & Coping</option>
                          <option value="Mindfulness & Peace">Mindfulness & Peace</option>
                          <option value="Success Story">Success Story</option>
                          <option value="Support & Advice">Support & Advice</option>
                          <option value="General Discussion">General Discussion</option>
                        </select>
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>Title (Optional):</label>
                        <input
                          type="text"
                          value={postTitle}
                          onChange={(e) => setPostTitle(e.target.value)}
                          placeholder="e.g. My daily breathing routine"
                          style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #d8b4fe', fontSize: '13.5px', background: '#fff' }}
                        />
                      </div>
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>Your Post / Reflection *</label>
                      <textarea
                        value={newPost}
                        onChange={(e) => setNewPost(e.target.value)}
                        placeholder="What's on your mind? Share your story, insights, or supportive thoughts..."
                        required
                        style={{
                          width: '100%',
                          padding: '18px',
                          borderRadius: '10px',
                          border: detectedProhibited.length > 0 ? '2px solid #ef4444' : '1.5px solid #e0e7ff',
                          fontFamily: 'inherit',
                          fontSize: '1.08rem',
                          minHeight: '120px',
                          marginBottom: '12px',
                          resize: 'vertical',
                          background: 'rgba(255,255,255,0.97)',
                          boxShadow: '0 1px 4px rgba(80,80,120,0.04)',
                        }}
                      />
                    </div>

                    {/* Prohibited words alert box */}
                    {isScanning ? (
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '14px' }}>
                        ⏳ Checking content safety guidelines...
                      </div>
                    ) : detectedProhibited.length > 0 ? (
                      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px' }}>
                        <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#991b1b' }}>
                          ⚠️ Prohibited words detected in your draft:
                        </div>
                        <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                          {detectedProhibited.map((w, idx) => (
                            <span key={idx} style={{ padding: '2px 8px', borderRadius: '4px', background: '#fee2e2', color: '#b91c1c', fontSize: '11.5px', fontWeight: 800 }}>
                              🚫 "{w.word}"
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : newPost.trim().length > 5 ? (
                      <div style={{ fontSize: '12px', color: '#059669', fontWeight: 600, marginBottom: '14px' }}>
                        🛡️ Clean: Text adheres to community safety standards.
                      </div>
                    ) : null}

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        type="submit"
                        disabled={isSubmitting || detectedProhibited.length > 0}
                        className="thm-btn"
                        style={{
                          fontWeight: 700,
                          fontSize: '1.08rem',
                          borderRadius: '8px',
                          opacity: detectedProhibited.length > 0 ? 0.6 : 1,
                          cursor: detectedProhibited.length > 0 ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {isSubmitting ? 'Posting...' : 'Post to Wall'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowPostForm(false);
                          setNewPost('');
                          setPostTitle('');
                          setDetectedProhibited([]);
                        }}
                        className="thm-btn"
                        style={{ backgroundColor: '#ccc', color: '#333', fontWeight: 700, borderRadius: '8px' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Posts List */}
              {loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  Loading community wall...
                </div>
              ) : posts.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', background: '#fff', borderRadius: '14px', border: '1px dashed #cbd5e1' }}>
                  <div style={{ fontSize: '36px', marginBottom: '8px' }}>💬</div>
                  <h4 style={{ margin: '0 0 6px 0', color: '#334155' }}>No community posts found</h4>
                  <p style={{ margin: 0, fontSize: '13.5px', color: '#64748b' }}>
                    Be the first member or practitioner to share your thoughts on the Community Wall!
                  </p>
                </div>
              ) : (
                posts.map((post) => {
                  const authorName = post.authorName || post.author?.name || 'Community Member';
                  const isLikedByMe = currentUserId && (post.likes || []).some(id => String(id) === String(currentUserId));

                  return (
                    <div key={post._id || post.id} className="post-card" style={{
                      visibility: 'visible',
                      opacity: 1,
                      background: 'rgba(255,255,255,0.92)',
                      backdropFilter: 'blur(4px)',
                      padding: '32px',
                      borderRadius: '18px',
                      boxShadow: '0 6px 32px rgba(80,80,120,0.13)',
                      marginBottom: '28px',
                      border: post.isVerifiedTherapist ? '1.5px solid #d8b4fe' : '1.5px solid #f3e9ff',
                      transition: 'box-shadow 0.2s, transform 0.2s',
                    }}>
                      {/* Post Header */}
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '18px' }}>
                        <div style={{
                          width: '54px',
                          height: '54px',
                          borderRadius: '50%',
                          background: post.isVerifiedTherapist ? 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' : 'linear-gradient(135deg, #e0e7ff 60%, #fbc2eb 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '18px',
                          boxShadow: '0 2px 8px rgba(124,58,237,0.08)',
                          color: '#fff',
                          fontWeight: 800,
                          fontSize: '20px',
                        }}>
                          {authorName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <h4 style={{ margin: 0, fontWeight: 700, fontSize: '1.08rem', color: '#2d2d4d' }}>
                              {authorName}
                            </h4>
                            {post.isVerifiedTherapist && (
                              <span style={{ fontSize: '11px', background: '#f3e8ff', color: '#7c3aed', padding: '2px 8px', borderRadius: '12px', fontWeight: 800, border: '1px solid #d8b4fe' }}>
                                ✓ Verified Practitioner
                              </span>
                            )}
                          </div>
                          <div style={{ marginTop: '2px' }}>
                            <span style={{ fontSize: '12px', color: '#999', marginRight: '10px' }}>
                              {post.createdAt ? new Date(post.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : post.date || 'Recent'}
                            </span>
                            <span style={{ fontSize: '12px', backgroundColor: '#ede9fe', color: '#7c3aed', padding: '3px 12px', borderRadius: '20px', fontWeight: 600 }}>
                              {post.category || 'General'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Post Content */}
                      {post.title && (
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '1.15rem', fontWeight: 800, color: '#1e293b' }}>
                          {post.title}
                        </h4>
                      )}
                      <p style={{ lineHeight: '1.7', marginBottom: '20px', color: '#444', fontSize: '1.08rem', whiteSpace: 'pre-wrap' }}>
                        {post.content}
                      </p>

                      {/* Post Footer */}
                      <div style={{
                        display: 'flex',
                        gap: '24px',
                        borderTop: '1px solid #eee',
                        paddingTop: '15px',
                        fontSize: '15px',
                        color: '#7c3aed',
                        fontWeight: 600,
                      }}>
                        <button
                          type="button"
                          onClick={() => handleLike(post)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: isLikedByMe ? '#dc2626' : '#7c3aed',
                            fontSize: '15px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <span>{isLikedByMe ? '❤️' : '👍'}</span>
                          <span>{post.likesCount || (post.likes || []).length || 0} Likes</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleComments(post._id)}
                          style={{
                            background: expandedComments[post._id] ? '#f3e8ff' : '#f8fafc',
                            border: expandedComments[post._id] ? '1px solid #c084fc' : '1px solid #e2e8f0',
                            borderRadius: '20px',
                            padding: '6px 14px',
                            cursor: 'pointer',
                            color: expandedComments[post._id] ? '#7c3aed' : '#475569',
                            fontSize: '13.5px',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <span>💬</span>
                          <span>{post.commentsCount || (post.comments || []).length || 0} {post.commentsCount === 1 ? 'Comment' : 'Comments'}</span>
                          <span style={{ fontSize: '11px', color: expandedComments[post._id] ? '#7c3aed' : '#94a3b8' }}>
                            {expandedComments[post._id] ? '▲ Close' : '▼ Reply'}
                          </span>
                        </button>
                      </div>

                      {/* Expandable Comments Drawer */}
                      {expandedComments[post._id] && (
                        <div style={{
                          marginTop: '18px',
                          padding: '18px 20px',
                          background: '#faf5ff',
                          borderRadius: '14px',
                          border: '1.5px solid #f3e8ff',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <h5 style={{ margin: 0, fontSize: '14px', fontWeight: 800, color: '#4c1d95', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>💬 Conversation & Replies</span>
                              <span style={{ fontSize: '12px', fontWeight: 700, color: '#7c3aed', background: '#edd8ff', padding: '1px 8px', borderRadius: '10px' }}>
                                {(post.comments || []).length}
                              </span>
                            </h5>
                            <span style={{ fontSize: '11.5px', color: '#6b7280' }}>
                              Safe & Moderated
                            </span>
                          </div>

                          {/* Existing Comments List */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                            {(!post.comments || post.comments.length === 0) ? (
                              <div style={{ padding: '14px 16px', background: '#fff', borderRadius: '10px', border: '1px dashed #d8b4fe', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                                💭 No comments yet. Be the first to share your encouragement or questions!
                              </div>
                            ) : (
                              post.comments.map((comment, cIdx) => {
                                const commentAuthorName = comment.authorName || 'Community Member';
                                const isPractitioner = comment.authorRole === 'physiotherapist' || comment.authorRole === 'therapist';
                                const isAdmin = comment.authorRole === 'admin';
                                const initial = commentAuthorName.charAt(0).toUpperCase() || 'M';
                                const isDeletable = canDeleteComment(comment, post);

                                return (
                                  <div
                                    key={comment._id || cIdx}
                                    style={{
                                      background: '#ffffff',
                                      borderRadius: '12px',
                                      padding: '12px 16px',
                                      border: isPractitioner ? '1px solid #d8b4fe' : '1px solid #f3e8ff',
                                      boxShadow: '0 1px 4px rgba(124,58,237,0.04)',
                                    }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{
                                          width: '32px',
                                          height: '32px',
                                          borderRadius: '50%',
                                          background: isPractitioner
                                            ? 'linear-gradient(135deg, #7c3aed, #4f46e5)'
                                            : 'linear-gradient(135deg, #a78bfa, #818cf8)',
                                          color: '#fff',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontWeight: 800,
                                          fontSize: '13px',
                                        }}>
                                          {initial}
                                        </div>
                                        <div>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                            <span style={{ fontWeight: 800, fontSize: '13.5px', color: '#1e293b' }}>
                                              {commentAuthorName}
                                            </span>
                                            {isPractitioner && (
                                              <span style={{ fontSize: '10.5px', fontWeight: 800, background: '#f3e8ff', color: '#7c3aed', padding: '1px 6px', borderRadius: '10px', border: '1px solid #d8b4fe' }}>
                                                ✓ Practitioner
                                              </span>
                                            )}
                                            {isAdmin && (
                                              <span style={{ fontSize: '10.5px', fontWeight: 800, background: '#f1f5f9', color: '#334155', padding: '1px 6px', borderRadius: '10px' }}>
                                                Admin
                                              </span>
                                            )}
                                            {!isPractitioner && !isAdmin && (
                                              <span style={{ fontSize: '10.5px', fontWeight: 600, background: '#ede9fe', color: '#6d28d9', padding: '1px 6px', borderRadius: '10px' }}>
                                                Client
                                              </span>
                                            )}
                                          </div>
                                          <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                                            {formatCommentDate(comment.createdAt)}
                                          </div>
                                        </div>
                                      </div>

                                      {isDeletable && (
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteComment(post._id, comment._id)}
                                          title="Delete this comment"
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#94a3b8',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.color = '#dc2626'}
                                          onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                                        >
                                          🗑️
                                        </button>
                                      )}
                                    </div>

                                    <p style={{ margin: '4px 0 0 42px', fontSize: '13.5px', color: '#334155', lineHeight: '1.55', whiteSpace: 'pre-wrap' }}>
                                      {comment.content}
                                    </p>
                                  </div>
                                );
                              })
                            )}
                          </div>

                          {/* Comment Input Box */}
                          {authSession?.token ? (
                            <div style={{ background: '#ffffff', borderRadius: '12px', padding: '12px 14px', border: '1.5px solid #d8b4fe' }}>
                              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                <div style={{
                                  width: '34px',
                                  height: '34px',
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #7c3aed, #ec4899)',
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 800,
                                  fontSize: '13.5px',
                                  flexShrink: 0,
                                }}>
                                  {currentUserName.charAt(0).toUpperCase()}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <textarea
                                    rows={2}
                                    placeholder={`Comment as ${currentUserName}...`}
                                    value={commentInputs[post._id] || ''}
                                    onChange={(e) => setCommentInputs({ ...commentInputs, [post._id]: e.target.value })}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleAddComment(post._id);
                                      }
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '8px 10px',
                                      borderRadius: '8px',
                                      border: '1px solid #cbd5e1',
                                      fontSize: '13px',
                                      lineHeight: '1.4',
                                      resize: 'vertical',
                                      fontFamily: 'inherit',
                                      outline: 'none',
                                      boxSizing: 'border-box',
                                    }}
                                  />

                                  {commentErrors[post._id] && (
                                    <div style={{ marginTop: '6px', fontSize: '12px', color: '#b91c1c', fontWeight: 600, background: '#fef2f2', padding: '6px 10px', borderRadius: '6px', border: '1px solid #fecaca' }}>
                                      ⚠️ {commentErrors[post._id]}
                                    </div>
                                  )}

                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                      Press Enter to post
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handleAddComment(post._id)}
                                      disabled={submittingCommentId === post._id || !commentInputs[post._id]?.trim()}
                                      style={{
                                        padding: '6px 16px',
                                        borderRadius: '8px',
                                        background: commentInputs[post._id]?.trim() ? '#7c3aed' : '#cbd5e1',
                                        color: '#fff',
                                        border: 'none',
                                        fontWeight: 700,
                                        fontSize: '12.5px',
                                        cursor: commentInputs[post._id]?.trim() ? 'pointer' : 'not-allowed',
                                        transition: 'all 0.2s ease',
                                      }}
                                    >
                                      {submittingCommentId === post._id ? 'Sending...' : 'Post Reply'}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div style={{
                              background: '#ffffff',
                              borderRadius: '12px',
                              padding: '12px 16px',
                              border: '1px dashed #d8b4fe',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '10px',
                            }}>
                              <span style={{ fontSize: '13px', color: '#64748b' }}>
                                Log in as a client to leave a comment or reply on this post.
                              </span>
                              <a
                                href="/login"
                                style={{
                                  padding: '6px 14px',
                                  borderRadius: '8px',
                                  background: '#7c3aed',
                                  color: '#fff',
                                  fontSize: '12px',
                                  fontWeight: 700,
                                  textDecoration: 'none',
                                }}
                              >
                                Log In to Comment
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              <div className="community-sidebar wow fadeInUp" style={{
                background: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(4px)',
                padding: '32px',
                borderRadius: '18px',
                boxShadow: '0 6px 32px rgba(80,80,120,0.13)',
                border: '1.5px solid #f3e9ff',
                position: 'sticky',
                top: '110px',
              }}>
                <h3 style={{ marginBottom: '18px', color: '#7c3aed', fontWeight: 800 }}>Community Safety Rules</h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <li style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '13.5px', color: '#475569' }}>
                    <span style={{ fontSize: '18px', color: '#059669' }}>✓</span>
                    <span><strong>Verified Practitioners:</strong> Insights from licensed therapists are highlighted with purple verified badges.</span>
                  </li>
                  <li style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '13.5px', color: '#475569' }}>
                    <span style={{ fontSize: '18px', color: '#ef4444' }}>🚫</span>
                    <span><strong>Prohibited Words Filter:</strong> Automatic moderation blocks vulgarity, harassment, and unauthorized medical claims.</span>
                  </li>
                  <li style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '13.5px', color: '#475569' }}>
                    <span style={{ fontSize: '18px', color: '#7c3aed' }}>🤝</span>
                    <span><strong>Respect & Empathy:</strong> Maintain a kind, supportive atmosphere for all individuals sharing their healing journeys.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Community Section End */}
    </>
  );
}

export default CommunityPage;
